import posthog from 'posthog-js';
import { getCsrfToken } from './csrf';

export class APIError extends Error {
  errors: Record<string, string[]>;
  statusCode: number;
  constructor(message: string, errors: Record<string, string[]> = {}, statusCode: number) {
    super(message);
    this.errors = errors;
    this.statusCode = statusCode;
  }
}

export function isAPIError(error: unknown): error is APIError {
  return error instanceof APIError;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: HeadersInit;
  body?: unknown;
}

export type ApiClientProps = {
  baseUrl: string;
  projectStageId?: number | undefined;
  headers?: HeadersInit | undefined;
};

export type PaginationParams = {
  ordering?: string;
  page_size?: number;
};

type APIResponse<T> = {
  data: T;
  metadata: {
    error: null;
    errors: Record<string, string[]>;
  };
};

type APIErrorResponse = {
  data: null;
  metadata: {
    error: string;
    errors: Record<string, string[]>;
  };
};

export type Paginated<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

export interface History {
  history_change_reason: string;
  history_date: string;
  history_id: number;
  history_type: string;
  history_user_id: number;
}
export type QueryParams = Record<string, string | number | (string | number)[]>;

export class ApiClient {
  public readonly projectStageId: number | undefined;
  private baseUrl: string;
  private headers: HeadersInit | undefined;

  constructor({ baseUrl, headers, projectStageId }: ApiClientProps) {
    this.baseUrl = baseUrl;
    this.projectStageId = projectStageId;
    this.headers = headers;
  }

  protected get _baseUrl(): string {
    return this.baseUrl;
  }

  private addPostHogHeaders(headers: Headers): void {
    // Only add headers in browser environment
    if (globalThis.window === undefined) return;

    try {
      // Check if PostHog is initialized (has a project API key set)
      if (!posthog.__loaded) {
        return;
      }

      // Get session ID and distinct ID from PostHog
      const sessionId = posthog.get_session_id();
      const distinctId = posthog.get_distinct_id();

      if (sessionId) {
        headers.set('X-POSTHOG-SESSION-ID', sessionId);
      }
      if (distinctId) {
        headers.set('X-POSTHOG-DISTINCT-ID', distinctId);
      }
    } catch {
      // Silently fail if PostHog is not available
    }
  }

  private async _request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    options: {
      body?: object | undefined;
      params?: QueryParams | undefined;
      version?: string | undefined;
      silentStatuses?: number[] | undefined;
    },
  ): Promise<T> {
    const { body, params, version = 'v1', silentStatuses } = options;

    const query = params ? `?${this.buildQueryParams(params)}` : '';

    const url = `${this.baseUrl}/${version ? version + '/' : ''}${path}${query}`;
    const fullHeaders = new Headers(this.headers);
    if (!(body instanceof FormData)) fullHeaders.set('Content-Type', 'application/json');
    if (this.headers === undefined && method !== 'GET') {
      fullHeaders.set('X-CSRFToken', await getCsrfToken(this.baseUrl));
    }

    // Add PostHog tracing headers for cross-platform tracking
    this.addPostHogHeaders(fullHeaders);

    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers: fullHeaders,
        credentials: 'include',
        // eslint-disable-next-line unicorn/no-null
        body: body instanceof FormData ? body : body ? JSON.stringify(body) : null,
      });
    } catch (error) {
      throw new APIError(`Network error while fetching ${url}: ${error}`, {}, 0);
    }

    if (!res.ok) {
      let error = 'Request failed';
      let errors = {};
      try {
        const response = (await res.json()) as APIErrorResponse;
        error = response.metadata.error;
        errors = response.metadata.errors;
      } catch {
        // ignore
      }
      if (!silentStatuses?.includes(res.status)) {
        console.error('API error', { error, errors, url, status: res.status });
      }
      throw new APIError(error, errors, res.status);
    }

    if (res.status === 204) return null as T;

    const response = (await res.json()) as APIResponse<T>;

    return response.data;
  }

  public _get<T>(
    path: string,
    params?: QueryParams,
    version: string = 'v1',
    options?: { silentStatuses?: number[] },
  ): Promise<T> {
    return this._request<T>('GET', path, {
      params,
      version,
      silentStatuses: options?.silentStatuses,
    });
  }

  public _post<T>(
    path: string,
    body?: object,
    params?: QueryParams,
    version: string = 'v1',
  ): Promise<T> {
    return this._request<T>('POST', path, { body, params, version });
  }

  public _put<T>(
    path: string,
    body: object,
    params?: QueryParams,
    version: string = 'v1',
  ): Promise<T> {
    return this._request<T>('PUT', path, { body, params, version });
  }

  public _patch<T>(
    path: string,
    body: object,
    params?: QueryParams,
    version: string = 'v1',
  ): Promise<T> {
    return this._request<T>('PATCH', path, { body, params, version });
  }

  public _delete(path: string, params?: QueryParams, version: string = 'v1'): Promise<boolean> {
    return this._request<boolean>('DELETE', path, { params, version });
  }

  private buildQueryParams(params: QueryParams) {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        for (const v of value) {
          searchParams.append(key, v.toString());
        }
      } else {
        searchParams.append(key, value.toString());
      }
    }

    return searchParams.toString();
  }
}
